import { useEffect, useMemo, useState } from "react";
import { EMPTY_OBJECT, QUERY_URL_AND, ROUTE, SEARCH_PAGE_QUERY_PARAM_SPLITTER, SEPARATOR } from "../misc";
// import { ProviderFilterWithDemo } from "./ProviderFilterWithDemo";
import _ from "lodash";
import { useSearchPageContext } from "../Context";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";
import { useProvidersMetadataWithCount } from "./useProvidersMetadataWithCount";
import { useProvidersSelectionMetadataWithCount } from "./useProvidersSelectionMetadataWithCount";

export type SearchParams = Record<string, string[]>;

type RawSeachParamSelections = Record<string, (string | "")[] | null | undefined>;
export interface ProviderFilterWithFetchProps {
    selections: RawSeachParamSelections;
}

const fromKeyValuePairsToSearchParams = (keyValuePairs?: { key: string; value: string[] }[]) => {
    if (!keyValuePairs || !keyValuePairs?.length) return EMPTY_OBJECT;

    return keyValuePairs.reduce<SearchParams>((prev, current) => {
        const { key, value } = current;
        prev[key] = value;
        return prev;
    }, {});
};

const searchQueryString = (searchParams: SearchParams) => {
    if (!searchParams) return undefined;

    return `${ROUTE.SEARCH_PAGE}?${_.join(
        _.keys(searchParams).map(key => {
            const paramStr = _.join(
                (searchParams[key] ?? []).filter(d => d != null && d.toString().trim() !== ""),
                SEARCH_PAGE_QUERY_PARAM_SPLITTER
            );
            return `${key}=${paramStr}`;
        }),
        QUERY_URL_AND
    )}`;
};
const useProviderFilterWithFetchBySelectionsObject = (selections: {
    rawSearchParams: RawSeachParamSelections;
    driver: string;
}) => {
    // it needs to load
    // 1. Provider type counts from solr
    // 2. Provider type metadata from Drupal site
    // 3. Connection size categories' counts from solr
    // 4. Connection size categories metadata from Drupal site
    // 5. Region counts from solr
    // 6. Region metadata from Drupal site
    // on Page load
    // 1. it needs to fetch load the above as defaults
    // 2. it needs to validate checked values as props
    //      2.1. if props are invalid, redirect
    //      2.2. if props are valid,
    //          2.2.1. Query to get results
    //          2.2.2. Set results to page state
    const history = useHistory();
    const [metadataLoading, setMetadataLoading] = useState(true);
    // const [searchParamsDirtyState, setSearchParamsDirtyState] = useState<SearchParams>(undefined);
    const { searchParams, setSearchParams, metadataWithCount, setMetadataWithCount } = useSearchPageContext();

    // sanitise the search params but not validate them against the metadata from API yet
    // console.log(selections);

    const selectionsRep = _.join(
        _.toPairs(selections?.rawSearchParams).map(([key, value]) => {
            return _.join(
                _.flattenDeep([key, value ?? []]).filter(d => d != null && d.toString().trim() !== ""),
                SEPARATOR
            );
        }),
        SEPARATOR
    );

    const searchParamsDirtyState = useMemo(
        () => {
            const dirtyState = fromKeyValuePairsToSearchParams(
                _.toPairs(selections?.rawSearchParams)
                    .map(([key, value]) => {
                        if (key == null || key.toString().trim() === "") return undefined;
                        return {
                            key,
                            value: (value ?? []).filter(d => d != null && d.toString().trim() !== "")
                        };
                    })
                    .filter(d => d != null)
            );

            // setSearchParamsDirtyState(dirtyState);
            return dirtyState;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectionsRep]
    );

    // load metadata with count at the start
    useProvidersMetadataWithCount({
        metadataWithCount,
        setMetadataLoading,
        setMetadataWithCount
    });

    // console.log("searchParamsDirtyState", searchParamsDirtyState, metadataWithCount);

    // validate dirty state search params against the metadata API
    useEffect(() => {
        if (!metadataWithCount || !searchParamsDirtyState) return;

        let newSearchParamsDirtyState: RawSeachParamSelections = {
            ...(searchParamsDirtyState ?? {})
        };

        _.keys(metadataWithCount).forEach(key => {
            if (!newSearchParamsDirtyState[key]?.length && metadataWithCount?.[key].defaults) {
                newSearchParamsDirtyState[key] = metadataWithCount?.[key].defaults;
            }
        });

        // fulfill defaults if they don't exist in search params dirty state
        const dirtyStateWithDefaults = _.toPairs(newSearchParamsDirtyState)
            .map(([key, value]) => {
                if (!metadataWithCount[key]) return undefined;

                return {
                    key,
                    value
                };
            })
            .filter(d => d !== undefined);

        const mediateState = dirtyStateWithDefaults.map(({ key, value }) => {
            const { options } = metadataWithCount[key];

            const searchParamValueToOptions = _.keyBy(options ?? [], d => d?.value);
            const newValue = value.filter(val => searchParamValueToOptions[val] != null);
            const isValid = newValue.length === value.length;

            return {
                key,
                value: newValue,
                isValid
            };
        });

        const isValid = mediateState.filter(({ isValid }) => isValid === true).length === mediateState.length;
        const cleanState = fromKeyValuePairsToSearchParams(mediateState);

        // not valid, redirect
        // valid, set clean states
        // if (!isValid) {
        // redirect
        // return;
        // }

        const queryStr = searchQueryString(cleanState);

        if (!isValid && queryStr != null) {
            history.push(queryStr);
            return;
        }

        if (isValid) {
            const sameAmount =
                _.flattenDeep(_.values(searchParams)).filter(d => d != null && d.trim() !== "")?.length ===
                _.flattenDeep(_.values(cleanState)).filter(d => d != null && (d as string).trim() !== "")?.length;

            if (!searchParams || !sameAmount) setSearchParams(cleanState);
        } else {
            setSearchParams(cleanState);
        }
    }, [metadataWithCount, searchParamsDirtyState, history, searchParams, setSearchParams]);

    // use search params, and metadatawithcount to update metadatawithcount (disabled field, validAmount field)

    useProvidersSelectionMetadataWithCount({
        searchParams,
        metadataWithCount,
        setMetadataLoading,
        setMetadataWithCount
    });

    // useEffect(() => {
    //     console.log("update", searchParams, metadataWithCount);
    // }, [searchParams, metadataWithCount]);

    const onSelectionChange = useMemo(() => {
        const _onChange = (key, value, checked) => {
            // combine new key, value, checked, redirect
            const newCleanState = fromKeyValuePairsToSearchParams(
                _.toPairs(searchParams).map(([keyState, valueState]) => {
                    if (keyState !== key) {
                        return {
                            key: keyState,
                            value: valueState
                        };
                    }

                    let newValueState = [];
                    if (checked) {
                        newValueState = _.uniq(
                            [value, ...(valueState ?? [])].filter(d => d != null && d.toString().trim() !== "")
                        );
                    } else {
                        newValueState = valueState.filter(d => d !== value);
                    }

                    return {
                        key: keyState,
                        value: newValueState
                    };
                })
            );

            // it shoud redirect, for testing, set state
            // setSearchParams(newCleanState);
            const queryStr = searchQueryString(newCleanState);
            if (queryStr == null) return;

            history.push(queryStr);
        };

        // return _.debounce(_onChange, 1000);
        return _onChange;
    }, [searchParams, history]);

    return {
        metadataLoading,
        metadataWithCount,
        searchParams,
        onSelectionChange
    };
};

export const useProviderFilterWithFetch = () => {
    const { search } = useLocation();
    const [rawSearchParams, setRawSearchParams] = useState<{
        rawSearchParams: RawSeachParamSelections;
        driver: string;
    }>({
        rawSearchParams: undefined,
        driver: undefined
    });
    const { metadataWithCount, metadataLoading, searchParams, onSelectionChange } =
        useProviderFilterWithFetchBySelectionsObject(rawSearchParams);

    useEffect(() => {
        if (!metadataWithCount || !search || search.toString().trim() === "") return;

        const params = search == null ? {} : queryString.parse(search);
        const newRawSearchParams = _.keys(metadataWithCount)
            .filter(key => key === "providerSize" || key === "providerRegion")
            .reduce<RawSeachParamSelections>((prev, key) => {
                prev[key] = _.split(params?.[key] ?? "", SEARCH_PAGE_QUERY_PARAM_SPLITTER) ?? [];
                return prev;
            }, {} as RawSeachParamSelections);

        // parse category driver from the url
        const categoryDriver =
            params?.["categoryDriver"] === "providerSize" || params?.["categoryDriver"] === "providerRegion"
                ? params?.["categoryDriver"]
                : "providerSize";

        setRawSearchParams({ rawSearchParams: newRawSearchParams, driver: categoryDriver });
    }, [metadataWithCount, search]);

    return {
        metadataLoading,
        metadataWithCount,
        searchParams,
        onSelectionChange
    };
};
